import React from 'react';
import clsx from 'clsx';
import { ReactComponent as InfoIcon } from '../../../_shared/assets/icons/info-icon.svg';
import { Input } from '../../../_shared/components/Input/Input';
import { KpiTableProps, KpiRow } from './types';
import styles from './FactsInputTableDesktop.module.scss';

const CURRENT_YEAR = 2026;

type FactField = 'fact6months' | 'fact9months' | 'fact12months';

function formatValue(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toString();
}

function toInputValue(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return value.toString();
}

function parseInputValue(raw: string): number | null {
  return raw.trim() === '' ? null : Number(raw);
}

export function FactsInputTableDesktop({
  rows,
  editable = false,
  onKpiClick,
  onMethodologyClick,
  onFactChange
}: KpiTableProps) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.thNum}>№</th>
          <th className={styles.thName}>Название KPI</th>
          <th className={styles.thUnit}>Ед. изм.</th>
          <th className={styles.thMethod}>Методика расчёта</th>
          <th className={styles.thNum}>План {CURRENT_YEAR}</th>
          <th className={styles.thNum}>Факт {CURRENT_YEAR}, 6 мес</th>
          <th className={styles.thNum}>Факт {CURRENT_YEAR}, 9 мес</th>
          <th className={styles.thNum}>Факт {CURRENT_YEAR}, 12 мес</th>
        </tr>
      </thead>

      <tbody>
        {rows.map((row, index) => (
          <KpiTableRow
            key={row.id}
            row={row}
            index={index}
            editable={editable}
            onKpiClick={onKpiClick}
            onMethodologyClick={onMethodologyClick}
            onFactChange={onFactChange}
          />
        ))}
      </tbody>
    </table>
  );
}

interface RowProps {
  row: KpiRow;
  index: number;
  editable?: boolean;
  onKpiClick?: (row: KpiRow) => void;
  onMethodologyClick?: (row: KpiRow) => void;
  onFactChange?: KpiTableProps['onFactChange'];
}

function KpiTableRow({
  row,
  index,
  editable,
  onKpiClick,
  onMethodologyClick,
  onFactChange
}: RowProps) {
  const currentYearData = row.years.find((y) => y.year === CURRENT_YEAR);

  if (!currentYearData) return null;

  const renderFactCell = (field: FactField) => (
    <td className={clsx(styles.td, editable && styles.editableCell)}>
      {editable ? (
        <Input
          label=""
          type="number"
          value={toInputValue(currentYearData[field])}
          onChange={(e) =>
            onFactChange?.(
              row.id,
              CURRENT_YEAR,
              field,
              parseInputValue(e.target.value)
            )
          }
          className={styles.editInput}
        />
      ) : (
        formatValue(currentYearData[field])
      )}
    </td>
  );

  return (
    <tr className={styles.row}>
      <td className={styles.tdNum}>{index + 1}</td>
      <td className={styles.tdName}>
        <button className={styles.nameLink} onClick={() => onKpiClick?.(row)}>
          {row.name}
        </button>
      </td>
      <td className={clsx(styles.td, styles.tdUnit)}>{row.unit}</td>
      <td className={styles.td}>
        <button
          className={styles.methodLink}
          onClick={() => onMethodologyClick?.(row)}
        >
          Методика&nbsp;
          <InfoIcon className={styles.infoIcon} />
        </button>
      </td>

      <td className={styles.td}>{formatValue(currentYearData.plan)}</td>

      {renderFactCell('fact6months')}
      {renderFactCell('fact9months')}
      {renderFactCell('fact12months')}
    </tr>
  );
}
